import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import cc from 'classcat'
import loadable from '@loadable/component'

import type { IconPluginData } from './IconSettings'
import { emptyIconPluginData, resolveIconColors } from '../../../../editorSidebarBlockTemplates'
import { useTranslate } from '../../../../../utils/translate'
import PluginPlaceholder from '../components/PluginPlaceholder/PluginPlaceholder'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './IconSettings'))

export default function IconPlugin({
  data: immutableData,
  editorMode,
  editorView,
  onEdit,
  onCancel,
  onDataChange,
  onSave,
}: Readonly<WorkspacePluginProps>): ReactElement | null {
  const data: IconPluginData = immutableData.toJS()
  const [isActive, setIsActive] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const t = useTranslate('interface')

  const pluginActiveClasses = cc([
    {
      'dali-grid-element-highlighted': isActive,
    },
  ])

  if (editorView) {
    return (
      <div className={pluginActiveClasses} ref={ref}>
        {data.type ? (
          renderIcon()
        ) : (
          <PluginPlaceholder iconClassName="plugin-placeholder-iconplugin" onAdd={() => onEdit()}>
            {t('components.iconElementComponent.addIconButton.label')}
          </PluginPlaceholder>
        )}
        <SettingsLayer
          open={editorMode === 'edit'}
          virtualRef={ref}
          side="right"
          onEscapeKeyDown={onCancel}
          className="dali-plugin-icon-settingslayer"
          onActiveStateChange={setIsActive}
        >
          {({ renderLayout }) => (
            <SettingsForm
              data={data}
              onDataChange={onDataChange}
              onSave={onSave}
              onCancel={onCancel}
              renderLayout={renderLayout}
            />
          )}
        </SettingsLayer>
      </div>
    )
  }

  if (!data.type) return null

  return renderIcon()

  function renderIcon() {
    const { color, backgroundColor, borderColor } = resolveIconColors(data)

    return (
      <div
        className={cc([
          'dali-plugin-icon',
          {
            'no-bg-or-border-styles':
              backgroundColor === emptyIconPluginData.backgroundColor &&
              data.backgroundSize === emptyIconPluginData.backgroundSize &&
              data.borderSize === emptyIconPluginData.borderSize,
          },
        ])}
        style={{
          '--alignment': data.alignment,
          '--spacing': data.spacing + 'px',
          '--sizing': data.sizing,
          '--color': color,
          '--backgroundSize': data.backgroundSize,
          '--backgroundRadius': data.backgroundRadius + '%',
          '--backgroundColor': backgroundColor,
          '--borderSize': data.borderSize + 'px',
          '--borderColor': borderColor,
        }}
      >
        {data.link ? (
          <a
            href={data.link}
            target={data.opentab ? '_blank' : undefined}
            rel={data.opentab ? 'noreferrer noopener' : undefined}
          >
            <span className={`ep-${data.type}-icon`} />
          </a>
        ) : (
          <span className={`ep-${data.type}-icon`} />
        )}
      </div>
    )
  }
}
